import { Flex, Stack } from '@mantine/core';
import { useState } from 'react';
import { useCreateTarget } from '../../../../../api/targets-client/targets-client.hooks';
import { TargetPayload } from '../../../../../api/targets-client/targets-client.type';
import { useTranslation } from 'react-i18next';
import { useProcessAnalysisStore } from '../../../../../store/process-analysis-store/process-analysis-store';
import { inkLight } from '../../../../../styles/design-tokens';
import { Button } from '../../../../../ui-library/button/button';
import { Icon } from '../../../../../ui-library/icon/icon';
import { Text } from '../../../../../ui-library/typography/typography';
import { TargetModal } from '../../../../adherence/targets/target-modal';
import { SideSectionTargetsItem } from './side-section-targets-item';
import { getTargetModalInitialData } from './side-section-targets.helpers';

export const SideSectionTargets = () => {
  const i18n = useTranslation();
  const selectedDataPoint = useProcessAnalysisStore((state) => state.selectedDataPoint);

  const [isTargetModalOpen, setIsTargetModalOpen] = useState(false);
  const { createTarget, isCreating } = useCreateTarget();

  const handleCreateTarget = async (target: Partial<TargetPayload>) => {
    try {
      await createTarget(target);
      setIsTargetModalOpen(false);
    } catch (error) {
      console.error('Failed to create target:', error);
    }
  };

  if (!selectedDataPoint) {
    return null;
  }

  const targets = selectedDataPoint.targets || [];

  const header = (
    <Text size="medium" weight="bold">
      {i18n.t('process_analysis.side_section.targets')}
    </Text>
  );

  if (targets.length === 0) {
    return (
      <>
        <Stack>
          {header}
          <Flex
            style={{
              padding: '0px 24px',
            }}
            align="center"
          >
            <Text
              size="small"
              color={inkLight}
              style={{
                lineHeight: 1.5,
                textAlign: 'center',
                maxWidth: '280px',
              }}
            >
              {i18n.t('process_analysis.side_section.create_targets_prompt')}
            </Text>
          </Flex>
          <Button
            size="sm"
            radius="xl"
            variant="outline"
            leftSection={<Icon name="add" color={inkLight} size={14} pointer />}
            onClick={() => setIsTargetModalOpen(true)}
          >
            {i18n.t('process_analysis.side_section.create_target')}
          </Button>
        </Stack>

        <TargetModal
          opened={isTargetModalOpen}
          onClose={() => setIsTargetModalOpen(false)}
          onSave={handleCreateTarget}
          initialData={getTargetModalInitialData(selectedDataPoint)}
          isCreating={true}
          isLoading={isCreating}
          includeDashboardMeasures={true}
        />
      </>
    );
  }

  return (
    <>
      <Stack>
        {header}
        {targets.map((target) => (
          <SideSectionTargetsItem key={target.id} target={target} measureId={selectedDataPoint.measure} />
        ))}
      </Stack>

      <TargetModal
        opened={isTargetModalOpen}
        onClose={() => setIsTargetModalOpen(false)}
        onSave={handleCreateTarget}
        initialData={getTargetModalInitialData(selectedDataPoint)}
        isCreating={true}
        isLoading={isCreating}
        includeDashboardMeasures={true}
      />
    </>
  );
};
